import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getLegalDocuments,
  deleteLegalDocument,
  getSignedMediaUrl,
} from "../api/privacyPolicyAndTermConditions";
import { getApiErrorMessage } from "../utils/apiError";

// stored image URLs may carry an (expired) signing query; strip it to recover
// the plain object URL, which we then re-sign for display.
const toPlainUrl = (url) => (url ? url.split(/\?|%3F|%253F/i)[0] : url);

// decodes text/escaped button tags (including forms and embedded scripts) to real HTML
const decodeButtonHtml = (html) => {
  if (!html) return "";

  let result = html;

  // 1. Convert escaped <a>...</a> wrapping buttons or in content
  result = result.replace(/(&lt;a[\s\S]*?&gt;)([\s\S]*?)(&lt;\/\s*a\s*&gt;)/gi, (match, openTag, content) => {
    const cleanOpen = openTag.replace(/<[^>]+>/g, "");
    const decodedOpen = cleanOpen
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, "&")
      .trim();
    return `${decodedOpen}${content}</a>`;
  });

  // 2. Convert escaped or colored <form>...<script>...</script>...</form>
  result = result.replace(/(&lt;form[\s\S]*?&gt;)([\s\S]*?)(&lt;\/\s*form\s*&gt;)/gi, (match, openTag, content) => {
    const cleanOpen = openTag.replace(/<[^>]+>/g, "").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, "&").trim();
    return `${cleanOpen}${content}</form>`;
  });
  result = result.replace(/(&lt;script[\s\S]*?&gt;)([\s\S]*?)(&lt;\/\s*script\s*&gt;)/gi, (match, openTag, content) => {
    const cleanOpen = openTag.replace(/<[^>]+>/g, "").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, "&").trim();
    return `${cleanOpen}${content}</script>`;
  });

  // 3. Match any button pattern, whether escaped (&lt;button...) or raw (<button...)
  result = result.replace(/(&lt;button[\s\S]*?&gt;)([\s\S]*?)(&lt;\/\s*button\s*&gt;)/gi, (match, openTag, content) => {
    const cleanOpen = openTag.replace(/<[^>]+>/g, "");
    let decodedOpen = cleanOpen
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, "&")
      .trim();

    if (!/type\s*=/i.test(decodedOpen)) {
      decodedOpen = decodedOpen.replace(/^<button/i, '<button type="button"');
    }

    const cleanContent = content.replace(/^<\/span>/, "").replace(/<span[^>]*>$/, "");
    return `${decodedOpen}${cleanContent}</button>`;
  });

  // 4. Convert any remaining un-paired &lt;button...&gt;, &lt;form...&gt;, &lt;script...&gt;
  result = result.replace(/&lt;button[\s\S]*?&gt;/gi, (fullMatch) => {
    const clean = fullMatch.replace(/<[^>]+>/g, "");
    let decoded = clean
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, "&")
      .trim();
    if (!/type\s*=/i.test(decoded)) {
      decoded = decoded.replace(/^<button/i, '<button type="button"');
    }
    return decoded;
  });
  result = result.replace(/&lt;form[\s\S]*?&gt;/gi, (fullMatch) => {
    return fullMatch.replace(/<[^>]+>/g, "").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, "&").trim();
  });
  result = result.replace(/&lt;script[\s\S]*?&gt;/gi, (fullMatch) => {
    return fullMatch.replace(/<[^>]+>/g, "").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, "&").trim();
  });
  result = result.replace(/&lt;\/\s*button\s*&gt;/gi, "</button>");
  result = result.replace(/&lt;\/\s*form\s*&gt;/gi, "</form>");
  result = result.replace(/&lt;\/\s*script\s*&gt;/gi, "</script>");
  result = result.replace(/&lt;\/\s*a\s*&gt;/gi, "</a>");

  // 5. Clean up any orphan spans that wrapped the button tags
  result = result.replace(/<span[^>]*>\s*(<button[^>]*>[\s\S]*?<\/button>)\s*<\/span>/gi, "$1");
  result = result.replace(/<span[^>]*>\s*(<form[^>]*>[\s\S]*?<\/form>)\s*<\/span>/gi, "$1");
  result = result.replace(/<span[^>]*>\s*(<button[^>]*>)/gi, "$1");
  result = result.replace(/(<\/button>)\s*<\/span>/gi, "$1");
  result = result.replace(/<span[^>]*>\s*(<form[^>]*>)/gi, "$1");
  result = result.replace(/(<\/form>)\s*<\/span>/gi, "$1");

  // 6. Ensure any bare Razorpay script (not inside <form>) is wrapped in <form>...</form>
  result = result.replace(/(?:<form[^>]*>[\s\S]*?<\/form>)|(<script[^>]*payment-button\.js[^>]*>[\s\S]*?<\/script>)/gi, (m, bareScript) => {
    if (bareScript) {
      return `<form>${bareScript}</form>`;
    }
    return m;
  });

  return result;
};

// executes embedded <script> tags (like Razorpay payment-button.js) within a DOM container
const runEmbeddedScripts = (container) => {
  if (!container) return;

  // 1. Ensure all Razorpay scripts are inside a <form> before executing to prevent Razorpay alerts
  const rzpScripts = container.querySelectorAll("script[data-payment_button_id], script[src*='payment-button.js']");
  rzpScripts.forEach((script) => {
    if (!script.closest("form") || script.parentElement?.tagName !== "FORM") {
      const form = document.createElement("form");
      script.parentNode.insertBefore(form, script);
      form.appendChild(script);
    }
  });

  // 2. Execute Razorpay scripts inside forms
  const forms = container.querySelectorAll("form");
  forms.forEach((form) => {
    const script = form.querySelector("script");
    if (!script) return;
    // If a payment button was already injected by Razorpay in this form, do not re-run
    if (form.querySelector(".razorpay-payment-button")) {
      return;
    }
    if (script.getAttribute("data-executed") === "true") {
      return;
    }
    const newScript = document.createElement("script");
    Array.from(script.attributes).forEach((attr) => {
      newScript.setAttribute(attr.name, attr.value);
    });
    newScript.setAttribute("data-executed", "true");
    newScript.async = true;
    if (script.innerHTML) {
      newScript.appendChild(document.createTextNode(script.innerHTML));
    }
    if (script.parentNode) {
      script.parentNode.replaceChild(newScript, script);
    }
  });

  // 3. Execute any other non-Razorpay standalone scripts
  const standaloneScripts = container.querySelectorAll("script:not(form script)");
  standaloneScripts.forEach((oldScript) => {
    if (oldScript.getAttribute("data-executed") === "true") return;
    if (oldScript.src && oldScript.src.includes("payment-button.js")) return;
    if (oldScript.hasAttribute("data-payment_button_id")) return;

    const newScript = document.createElement("script");
    Array.from(oldScript.attributes).forEach((attr) => {
      newScript.setAttribute(attr.name, attr.value);
    });
    newScript.setAttribute("data-executed", "true");
    newScript.async = true;
    if (oldScript.innerHTML) {
      newScript.appendChild(document.createTextNode(oldScript.innerHTML));
    }
    if (oldScript.parentNode) {
      oldScript.parentNode.replaceChild(newScript, oldScript);
    }
  });
};

const ManagePolicy = () => {
  const navigate = useNavigate();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState(null);

  // preview dialog state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewPolicy, setPreviewPolicy] = useState(null);
  const previewContentRef = useRef(null);

  useEffect(() => {
    if (previewOpen && !previewLoading && previewContentRef.current) {
      const timer = setTimeout(() => {
        runEmbeddedScripts(previewContentRef.current);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [previewOpen, previewLoading, previewPolicy]);

  const fetchPolicies = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getLegalDocuments();

      // be tolerant of different response shapes
      const data = res?.data ?? res;
      const list =
        data?.documents ??
        data?.policies ??
        data?.items ??
        data?.results ??
        (Array.isArray(data) ? data : []);

      setPolicies(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Failed to fetch policies:", err);
      setPolicies([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPolicies();
  }, [fetchPolicies]);

  const handleEditPolicy = (policy) => {
    const id = policy.id ?? policy._id;
    if (!id) return;
    // pass the full row so the edit form prefills instantly without refetching
    navigate(`/edit-policy/${id}`, { state: { policy } });
  };

  // the bucket is private, so embedded image URLs 403 on their own — swap each
  // src for a fresh signed URL so images render in the preview.
  const signContentImages = async (html) => {
    const wrapper = document.createElement("div");
    wrapper.innerHTML = html || "";
    const imgs = Array.from(wrapper.querySelectorAll("img"));
    await Promise.all(
      imgs.map(async (img) => {
        const plain = toPlainUrl(img.getAttribute("src") || "");
        if (!plain) return;
        try {
          const res = await getSignedMediaUrl(plain);
          const signed = res?.data?.signedUrl || res?.signedUrl;
          if (signed) img.setAttribute("src", signed);
        } catch (e) {
          console.error("Failed to sign preview image:", e);
        }
      })
    );
    return wrapper.innerHTML;
  };

  const handlePreview = async (policy) => {
    setPreviewOpen(true);
    setPreviewPolicy(null);
    try {
      setPreviewLoading(true);
      const content = policy.content || policy.contentHtml || "";
      const signedContent = await signContentImages(content);
      setPreviewPolicy({ ...policy, content: signedContent });
    } catch (err) {
      console.error("Failed to load policy preview:", err);
      toast.error(getApiErrorMessage(err, "Failed to load preview ❌"));
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDeleteClick = (policy) => {
    const id = policy.id ?? policy._id;
    if (!id) return;
    setPolicyToDelete(policy);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!policyToDelete) return;
    const id = policyToDelete.id ?? policyToDelete._id;
    if (!id) return;

    try {
      setDeletingId(id);
      const res = await deleteLegalDocument(id);

      if (res?.success === false) {
        throw new Error(res?.message || "Failed to delete policy ❌");
      }

      toast.success("Policy deleted successfully!");
      setDeleteDialogOpen(false);
      setPolicyToDelete(null);
      await fetchPolicies();
    } catch (err) {
      console.error("Failed to delete policy:", err);
      toast.error(getApiErrorMessage(err, "Failed to delete policy ❌"));
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (value) => {
    if (!value) return "-";
    try {
      return new Date(value).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return "-";
    }
  };

  // Neutralize any fill/stroke colors the author baked into the SVG so the
  // listing always shows the icon in a single flat color. Every explicit color
  // becomes `currentColor` (driven by the container below); `none` is kept as-is
  // because outline icons rely on it (fill: none + colored stroke).
  const forceMonochromeSvg = (svg) => {
    if (!svg) return svg;
    return svg
      // fill="..." / stroke="..." presentation attributes
      .replace(/(fill|stroke)\s*=\s*(["'])(.*?)\2/gi, (match, prop, quote, value) =>
        value.trim().toLowerCase() === "none" ? match : `${prop}=${quote}currentColor${quote}`
      )
      // fill:...; / stroke:...; inside a style="" attribute
      .replace(/(fill|stroke)\s*:\s*([^;"']+)/gi, (match, prop, value) =>
        value.trim().toLowerCase() === "none" ? match : `${prop}:currentColor`
      );
  };

  // sanitize SVG to strip any scripts or event handlers before rendering
  const sanitizeSvg = (svg) => {
    if (!svg || !svg.trim()) return "";
    try {
      const doc = new DOMParser().parseFromString(svg, "image/svg+xml");
      if (doc.querySelector("parsererror")) return "";
      const root = doc.documentElement;
      if (!root || root.nodeName.toLowerCase() !== "svg") return "";

      const DISALLOWED = new Set([
        "script",
        "foreignobject",
        "iframe",
        "frame",
        "frameset",
        "object",
        "embed",
        "applet",
        "meta",
        "link",
        "style",
        "audio",
        "video",
        "source",
        "base",
        "form",
        "input",
        "button",
      ]);

      const all = doc.querySelectorAll("*");
      for (const el of all) {
        const tag = (el.tagName || "").toLowerCase().replace(/^.*:/, "");
        if (DISALLOWED.has(tag)) {
          el.remove();
          continue;
        }
        for (let i = el.attributes.length - 1; i >= 0; i--) {
          const attr = el.attributes[i];
          const name = (attr.name || "").toLowerCase();
          const val = (attr.value || "").toLowerCase().replace(/\s+/g, "");
          if (
            /^on[a-z0-9_-]/i.test(name) ||
            val.includes("javascript:") ||
            val.includes("vbscript:") ||
            val.includes("data:text/html")
          ) {
            el.removeAttribute(attr.name);
          }
        }
      }
      return root.outerHTML;
    } catch {
      return "";
    }
  };

  // policy.icon holds inline <svg> markup (stored at 24x24). Render it as an
  // actual icon rather than showing the raw tag code.
  const renderIcon = (svg) => {
    if (!svg || !svg.trim()) return "-";
    const cleanedSvg = sanitizeSvg(svg);
    if (!cleanedSvg) return "-";
    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 24,
          height: 24,
          color: "#000",
          "& svg": { width: 24, height: 24, display: "block" },
        }}
        dangerouslySetInnerHTML={{ __html: forceMonochromeSvg(cleanedSvg) }}
      />
    );
  };

  const renderTruncatedCell = (value, maxWidth = 240) => {
    const text = value || "-";
    const isFallback = text === "-";

    return (
      <Tooltip title={isFallback ? "" : text} arrow placement="top">
        <Box
          component="span"
          sx={{
            display: "block",
            maxWidth,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {text}
        </Box>
      </Tooltip>
    );
  };

  return (
    <Box p={3} bgcolor="#f5f5f5" minHeight="100vh">
      {/* Top Bar */}
      <Box display="grid" gridTemplateColumns="1fr auto 1fr" alignItems="center" mb={3}>
        <Box>
          <Button
            variant="contained"
            onClick={() => navigate("/Dashboard")}
            sx={{
              background: "linear-gradient(90deg, #7b1fa2 0%, #9c27b0 100%)",
              textTransform: "none",
              px: 3,
            }}
          >
            Back to Dashboard
          </Button>
        </Box>

        <Typography variant="h5" fontWeight={600} textAlign="center">
          Manage Policy
        </Typography>

        <Box textAlign="right">
          <Button
            variant="contained"
            onClick={() => navigate("/create-policy")}
            sx={{ textTransform: "none", backgroundColor: "#1e88e5" }}
          >
            Create Policy
          </Button>
        </Box>
      </Box>

      <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
            <CircularProgress />
          </Box>
        ) : !policies.length ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="30vh">
            <Typography align="center" variant="h6" color="text.secondary">
              No policies available
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 600 } }}>
                  <TableCell>#</TableCell>
                  <TableCell>Icon</TableCell>
                  <TableCell>Policy Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {policies.map((policy, index) => {
                  const id = policy.id ?? policy._id;
                  return (
                    <TableRow
                      key={id ?? index}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleEditPolicy(policy)}
                    >
                      <TableCell>{index + 1}</TableCell>
                      <TableCell sx={{ maxWidth: 160 }}>
                        {renderIcon(policy.icon)}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>
                        {renderTruncatedCell(policy.type || policy.name, 320)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={policy.status || "-"}
                          size="small"
                          color={policy.status === "published" ? "success" : "default"}
                          sx={{ textTransform: "capitalize" }}
                        />
                      </TableCell>
                      <TableCell>{formatDate(policy.createdAt || policy.created_at)}</TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Box display="flex" gap={1} justifyContent="center">
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            startIcon={<VisibilityIcon fontSize="small" />}
                            onClick={() => handlePreview(policy)}
                            sx={{ textTransform: "none" }}
                          >
                            Preview
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcon fontSize="small" />}
                            onClick={() => handleEditPolicy(policy)}
                            sx={{ textTransform: "none" }}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon fontSize="small" />}
                            disabled={deletingId === id}
                            onClick={() => handleDeleteClick(policy)}
                            sx={{ textTransform: "none" }}
                          >
                            {deletingId === id ? "Deleting..." : "Delete"}
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Policy</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete this policy?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
            No
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview dialog — shows the policy the way a user would see it in the web app */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" fontWeight={600}>
            Policy Preview
          </Typography>
          <IconButton onClick={() => setPreviewOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: "#fff" }}>
          {previewLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="30vh">
              <CircularProgress />
            </Box>
          ) : previewPolicy ? (
            <Box sx={{ maxWidth: 800, mx: "auto", py: 2 }}>
              <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                {previewPolicy.icon?.trim() && (
                  <Box
                    sx={{
                      display: "inline-flex",
                      width: 28,
                      height: 28,
                      color: "#374151",
                      "& svg": { width: 28, height: 28, display: "block" },
                    }}
                    dangerouslySetInnerHTML={{ __html: previewPolicy.icon }}
                  />
                )}
                <Typography variant="h4" fontWeight={700}>
                  {previewPolicy.type || previewPolicy.name}
                </Typography>
              </Box>

              <Box display="flex" gap={1} alignItems="center" mb={3}>
                <Chip
                  label={previewPolicy.status || "draft"}
                  size="small"
                  color={previewPolicy.status === "published" ? "success" : "default"}
                  sx={{ textTransform: "capitalize" }}
                />
                {(previewPolicy.createdAt || previewPolicy.created_at) && (
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(previewPolicy.createdAt || previewPolicy.created_at)}
                  </Typography>
                )}
              </Box>

              {/* render the stored HTML the way the web app does: lists with markers,
                  styled blockquotes, and borderless tables */}
              <Box
                ref={previewContentRef}
                sx={{
                  lineHeight: 1.7,
                  fontSize: "15px",
                  color: "#374151",
                  "& img": { maxWidth: "100%", borderRadius: 1, my: 1 },
                  "& h1": { fontSize: "1.6rem", fontWeight: 700, mt: 2 },
                  "& h2": { fontSize: "1.2rem", fontWeight: 600, mt: 3, mb: 1 },
                  "& p": { my: 1 },
                  "& ul": { listStyleType: "disc", pl: 5, my: 1 },
                  "& ol": { listStyleType: "decimal", pl: 5, my: 1 },
                  "& li": { my: 0.5 },
                  "& a": { color: "#2563eb", textDecoration: "underline" },
                  "& blockquote": {
                    borderLeft: "4px solid #9c27b0",
                    my: 1.5,
                    py: 0.5,
                    px: 2,
                    color: "#555",
                    fontStyle: "italic",
                    background: "#faf5fc",
                  },
                  // match the web app: tables render borderless but keep cell padding
                  "& table": { width: "100%", borderCollapse: "collapse", my: 1.5 },
                  "& td, & th": {
                    padding: "8px",
                    verticalAlign: "top",
                    border: "none !important",
                  },
                  "& button": {
                    cursor: "pointer",
                    padding: "8px 18px",
                    borderRadius: "6px",
                    border: "1px solid #9c27b0",
                    backgroundColor: "#9c27b0",
                    color: "#ffffff",
                    fontSize: "14px",
                    fontWeight: 600,
                    display: "inline-block",
                    my: 0.5,
                    fontFamily: "inherit",
                    "&:hover": {
                      opacity: 0.9,
                    },
                  },
                }}
                dangerouslySetInnerHTML={{ __html: decodeButtonHtml(previewPolicy.content || "") }}
              />
            </Box>
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ManagePolicy;
